import React, { useState, useMemo, useRef } from "react";
import StatWrapper from "../shared/StatWrapper";
import Text from "hardcover-ui/components/Text";
import {
  DropdownMenu,
  DropdownMenuTrigger,
  DropdownMenuContent,
  DropdownMenuItem,
} from "hardcover-ui/components/Dropdown";
import Button from "hardcover-ui/components/Button";
import FilterIcon from "icons/solid/filter.svg";
import { Checkbox } from "hardcover-ui/components/Checkbox";
import Icon from "hardcover-ui/components/Icon";
import { usePage } from "@inertiajs/react";
import { UserStatsPageProps } from "pages/clientverse/users/stats/index.props";
import BookCategories, { BookCategoryType } from "constants/BookCategories";
import ReadingFormatType, { ReadingFormats } from "types/ReadingFormatType";
import { StatsSerializersBooksReadByYearYear } from "types/serializers";
import { parsedDateToDate } from "lib/dateUtils";
import LayerCake, { useLayerCake } from "components/viz/LayerCake";
import { tickFormatForDateDomain } from "components/viz/utils";
import AxisX from "components/viz/AxisX";
import { useChartTheme } from "../contexts/ChartThemeContext";
import Heading from "hardcover-ui/components/Heading";
// import { ScaleTime, scaleTime } from "d3-scale";

interface YearlyReadingStatProps {
  size?: "sm" | "md" | "lg" | "xl";
}

function initialSelectedCategories(categoryIds: number[] = []) {
  return BookCategories.filter((category) =>
    categoryIds.includes(category.value)
  );
}

function initialSelectedFormats(formatIds: number[] = []) {
  return ReadingFormats.filter((readingFormat) =>
    formatIds.includes(readingFormat.id)
  );
}

function calculateGradient(index: number, total: number, isActive: boolean) {
  const baseColor = isActive
    ? "var(--chart-colorful-2)"
    : "var(--chart-mono-1)";
  const opacity = isActive ? 1 : 0.5 + (index / total) * 0.5;
  return `color-mix(in srgb, ${baseColor}, transparent ${(1 - opacity) * 100}%)`;
}

function YearlyReadingChartBars({
  data,
}: {
  data: StatsSerializersBooksReadByYearYear[];
}) {
  const { height, rangeScale, padding, domainBand, active, setActive } =
    useLayerCake();

  const hideTimeout = useRef<NodeJS.Timeout | null>(null);

  const hideTooltip = () => {
    if (hideTimeout.current) {
      clearTimeout(hideTimeout.current);
    }
    hideTimeout.current = setTimeout(() => {
      setActive(null);
    }, 100);
  };

  return (
    <g transform={`translate(${padding.left},${padding.top})`}>
      {data.map((period, index) => (
        <rect
          key={period.label}
          x={domainBand(period.label)}
          y={rangeScale(period.value || 0)}
          width={domainBand.bandwidth()}
          height={height - padding.bottom - rangeScale(period.value || 0)}
          rx={2}
          fill={calculateGradient(
            index,
            data.length,
            active?.label === period.label
          )}
          className="transition-all hover:opacity-80"
          onMouseEnter={() => {
            if (hideTimeout.current) {
              clearTimeout(hideTimeout.current);
              hideTimeout.current = null;
            }
            setActive(period);
          }}
          onMouseLeave={hideTooltip}
        />
      ))}
    </g>
  );
}

function YearlyReadingChartSvg({
  data,
}: {
  data: StatsSerializersBooksReadByYearYear[];
}) {
  const { width, height, domain } = useLayerCake();

  const formatTick = useMemo(() => {
    return tickFormatForDateDomain(domain);
  }, [domain]);

  return (
    <svg
      width="100%"
      height="100%"
      viewBox={`0 0 ${width} ${height}`}
      preserveAspectRatio="none"
      className="overflow-visible"
    >
      <AxisX
        ticks={domain}
        baseline
        tickMarks
        snapTicks
        formatTick={formatTick}
      />
      <YearlyReadingChartBars data={data} />
    </svg>
  );
}

function TooltipText() {
  const { active } = useLayerCake();
  const { theme } = useChartTheme();

  if (!active) return null;

  const color =
    theme === "colorful" ? "var(--chart-colorful-2)" : "var(--chart-mono-1)";

  return (
    <div
      className="absolute pointer-events-none"
      style={{
        left: active.x,
        top: active.y,
        transform: "translate(-50%, -100%)",
        marginTop: "-8px",
      }}
    >
      <div
        className="px-2 py-1 rounded text-white text-sm whitespace-nowrap"
        style={{ backgroundColor: color }}
      >
        {active.label}: {active.value || 0} books
      </div>
    </div>
  );
}

function YearlyReadingChart({
  data,
}: {
  data: StatsSerializersBooksReadByYearYear[];
}) {
  if (!data || data.length === 0) return null;

  const padding = { bottom: 30, top: 0, left: 0, right: 0 };
  const domain: [number, number] = useMemo(() => {
    const startDate = parsedDateToDate(data[0]?.from || new Date());
    const endDate = parsedDateToDate(data[data.length - 1]?.to || new Date());
    return [startDate.getTime(), endDate.getTime()];
  }, [data]);

  const range: [number, number] = useMemo(() => {
    const maxBooks = Math.max(
      ...data.map((period) => period.userBookReads?.length || 0)
    );
    return [0, maxBooks];
  }, [data]);

  // function generateDomainScale({ width }: { width: number }): ScaleTime<number, number, never> {
  //   const startDate = parsedDateToDate(data[0].from);
  //   const endDate = parsedDateToDate(data[data.length-1].to);
  //   return scaleTime().domain([startDate, endDate]).range([0, width-padding.left-padding.right]);
  // }

  return (
    <LayerCake
      maxHeight={200}
      width={300}
      height={230}
      domain={domain}
      range={range}
      padding={padding}
      points={data}
      // generateDomainScale={generateDomainScale}
    >
      <YearlyReadingChartSvg data={data} />
      <TooltipText />
    </LayerCake>
  );
}

const YearlyReadingStat: React.FC<YearlyReadingStatProps> = ({ size }) => {
  const { stats } = usePage<UserStatsPageProps>().props;
  const { booksReadByYear: data } = stats;

  if (!data || !data.booksByYear || data.booksByYear.length === 0) {
    return (
      <StatWrapper title="Yearly Reading" size={size}>
        <div className="flex flex-col gap-4 w-full h-full">
          <Heading level={3} size="sm">
            No yearly reading data available
          </Heading>
        </div>
      </StatWrapper>
    );
  }

  const possibleCategories = initialSelectedCategories(
    data.initialCategories || []
  );
  const [selectedCategories, setSelectedCategories] =
    useState<BookCategoryType[]>(possibleCategories);
  const possibleFormats = initialSelectedFormats(data.initialFormats || []);
  const [selectedFormats, setSelectedFormats] =
    useState<ReadingFormatType[]>(possibleFormats);

  const filteredData = useMemo(() => {
    if (!data.booksByYear) return [];

    // Either is set to nothing
    if (selectedCategories.length === 0 || selectedFormats.length === 0) {
      if (selectedCategories.length === 0) {
        setSelectedCategories(possibleCategories);
      }
      if (selectedFormats.length === 0) {
        setSelectedFormats(possibleFormats);
      }
      return data.booksByYear;
    }

    // Both are set to everything
    if (
      selectedCategories.length === possibleCategories.length &&
      selectedFormats.length === possibleFormats.length
    ) {
      return data.booksByYear;
    }

    const selectedCategoryIds = selectedCategories.map(
      (category) => category.value
    );
    const selectedFormatIds = selectedFormats.map((category) =>
      Number(category.id)
    );

    return data.booksByYear.map((period) => {
      if (!period.userBookReads) {
        return {
          ...period,
          value: 0,
          userBookReads: [],
        };
      }

      // Only include userBookReads that match both criteria
      const userBookReads = period.userBookReads.filter((userBookRead) => {
        return (
          selectedCategoryIds.includes(userBookRead.bookCategoryId) &&
          selectedFormatIds.includes(userBookRead.readingFormatId)
        );
      });

      return {
        ...period,
        value: userBookReads.length,
        userBookReads,
      };
    });
  }, [
    selectedCategories,
    selectedFormats,
    data.booksByYear,
    possibleCategories,
    possibleFormats,
  ]);

  return (
    <StatWrapper title="Yearly Reading" size={size}>
      <div className="flex flex-col gap-4 w-full h-full">
        <div className="flex gap-2 w-full z-10">
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="tertiary"
                className="w-fit justify-between"
                size="sm"
              >
                <div className="flex items-center gap-2">
                  <Icon icon={FilterIcon} size="sm" />
                  <span>Book Type</span>
                </div>
                <Text variant="secondary" size="sm">
                  {selectedCategories.length} selected
                </Text>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[200px]">
              {possibleCategories.map((category) => (
                <DropdownMenuItem
                  key={category.label}
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    if (selectedCategories.includes(category)) {
                      setSelectedCategories(
                        selectedCategories.filter((t) => t !== category)
                      );
                    } else {
                      setSelectedCategories([...selectedCategories, category]);
                    }
                  }}
                >
                  <Checkbox
                    checked={selectedCategories.includes(category)}
                    onCheckedChange={() => {}}
                    className="pointer-events-none"
                  />
                  <Text>{category.label}</Text>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="tertiary"
                className="w-fit justify-between"
                size="sm"
              >
                <div className="flex items-center gap-2">
                  <Icon icon={FilterIcon} size="sm" />
                  <span>Format</span>
                </div>
                <Text variant="secondary" size="sm">
                  {selectedFormats.length} selected
                </Text>
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent className="w-[200px]">
              {possibleFormats.map((format) => (
                <DropdownMenuItem
                  key={format.id}
                  className="flex items-center gap-2 cursor-pointer"
                  onClick={(e) => {
                    e.preventDefault();
                    if (selectedFormats.includes(format)) {
                      setSelectedFormats(
                        selectedFormats.filter((f) => f !== format)
                      );
                    } else {
                      setSelectedFormats([...selectedFormats, format]);
                    }
                  }}
                >
                  <Checkbox
                    checked={selectedFormats.includes(format)}
                    onCheckedChange={() => {}}
                    className="pointer-events-none"
                  />
                  <Text>{format.format}</Text>
                </DropdownMenuItem>
              ))}
            </DropdownMenuContent>
          </DropdownMenu>
        </div>

        <YearlyReadingChart data={filteredData} />
      </div>
    </StatWrapper>
  );
};

export default YearlyReadingStat;
